import {
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  ViewEncapsulation,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { ActivatedRoute, Router, RouterOutlet } from "@angular/router";
import { FuseFullscreenComponent } from "@fuse/components/fullscreen";
import { FuseLoadingBarComponent } from "@fuse/components/loading-bar";
import {
  FuseNavigationItem,
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from "@fuse/components/navigation";
import { FuseMediaWatcherService } from "@fuse/services/media-watcher";
import { NavigationService } from "app/core/navigation/navigation.service";
import { Navigation } from "app/core/navigation/navigation.types";
import { UserService } from "app/core/user/user.service";
import { User } from "app/core/user/user.types";
import { LanguagesComponent } from "app/layout/common/languages/languages.component";
import { MessagesComponent } from "app/layout/common/messages/messages.component";
import { NotificationsComponent } from "app/layout/common/notifications/notifications.component";
import { QuickChatComponent } from "app/layout/common/quick-chat/quick-chat.component";
import { SearchComponent } from "app/layout/common/search/search.component";
import { ShortcutsComponent } from "app/layout/common/shortcuts/shortcuts.component";
import { UserComponent } from "app/layout/common/user/user.component";
import { concat, filter, map, Subject, takeUntil, tap } from "rxjs";
import { environment } from "../../../../../environments/environment";
import { RoleDTO } from "../../../../modules/admin/roles/dto/role.dto";
import { TranslateService } from "@ngx-translate/core";
import { TranslateModule } from "@ngx-translate/core";
import { MatMenuModule } from "@angular/material/menu";
import { translate } from "@ngneat/transloco";

@Component({
  selector: "classy-layout",
  templateUrl: "./classy.component.html",
  styleUrls: [],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    TranslateModule,
    MatMenuModule,
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    NotificationsComponent,
    UserComponent,
    MatIconModule,
    MatButtonModule,
    RouterOutlet,
  ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy, OnChanges {
  isScreenSmall: boolean;
  navigation: Navigation;
  user: User = {
    id: "",
    name: "",
    lastname: "",
    avatar: "",
    email: "",
    roles: [],
    status: "online",
  };
  urlFile = `${environment.urlFiles}/`;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  changed = signal(new Date());
  languages = [
    { code: "es", name: "Español", flag: "es" },
    { code: "en", name: "English", flag: "us" },
  ];
  currentLanguage: { code: string; name: string; flag: string };

  /**
   * Constructor
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _navigationService: NavigationService,
    private _userService: UserService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
    private translateService: TranslateService
  ) {}
  ngOnChanges(changes: SimpleChanges): void {
    throw new Error("Method not implemented.");
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this.translateService.onLangChange
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((event) => {
        this._navigationService.navigation$
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe((navigation: Navigation) => {
            navigation.default.map((n) => {
              n.title = this.translateService.instant(n.titleLang);
              if (n.children) {
                n.children.map((c) => {
                  c.title = this.translateService.instant(c.titleLang);
                  return c;
                });
              }
              return n;
            });
            this.navigation = navigation;
            this.changed.set(new Date());
          });
      });
    // Subscribe to navigation data

    // Subscribe to the user service
    this._userService.user$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((user: User) => {
        this.user = user;
      });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes("md");
      });

    // Establece el idioma actual
    const currentLang = this.translateService.currentLang || "es";
    this.currentLanguage =
      this.languages.find((lang) => lang.code === currentLang) ||
      this.languages[0];
    this.changeLanguage(currentLang);
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name
      );

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  changeLanguage(code: string): void {
    // Cambia el idioma
    this.translateService.use(code);
    this.currentLanguage =
      this.languages.find((lang) => lang.code === code) || this.languages[0];
  }
}
